import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from '../hooks/useForm';
import { useLogin } from '../hooks/useLogin';
import '../auth.css';
import MainHeader from '../header/MainHeader';
import Footer from '../footer/Footer';
import { useGoogleAuth } from '../hooks/useGoogleAuth';
import { storage } from '../utils/storage';
import { getRoleRedirectPath } from '../utils/userUtils';

export default function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const { login, loading, error } = useLogin();
  const { startGoogleSignIn } = useGoogleAuth({
    onSuccess: () => {
      const user = storage.getUserData();
      const redirectPath = getRoleRedirectPath(user?.role);
      navigate(redirectPath);
    },
    onError: () => {},
  });

  const { values, handleChange, handleSubmit } = useForm({
    initialValues: {
      email: '',
      password: '',
    },
    onSubmit: async formData => {
      await login(formData);
    },
  });

  return (
    <div className="auth-page-light home-page-light">
      <MainHeader />
      
      <main className="auth-main">
        <div className="auth-visual-bg"></div>
        
        <div className="auth-container">
          <div className="auth-glass-card">
            {/* Form Side */}
            <div className="auth-form-side">
              <div className="auth-nav-tabs">
                <button className="auth-nav-link active">{t('login.tab_login')}</button>
                <button className="auth-nav-link" onClick={() => navigate('/register')}>{t('login.tab_register')}</button>
              </div>

              <h2>{t('login.title')}</h2>
              <p className="auth-subtitle">{t('login.subtitle')}</p>

              <div className="social-btns">
                <button type="button" className="btn-social" onClick={startGoogleSignIn}>
                  <img src="https://w7.pngwing.com/pngs/702/463/png-transparent-favicon-google-logo-new-new-google-logo-icon-thumbnail.png" width="20" alt="Google" />
                  Google
                </button>
                <button type="button" className="btn-social">
                  <img src="https://cdn-icons-png.flaticon.com/512/179/179309.png" width="20" alt="Apple" />
                  Apple
                </button>
              </div>

              <div className="divider-text">{t('login.or_with_email')}</div>

              {error && <div className="alert alert-danger border-0 rounded-3 mb-4">{error}</div>}

              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label className="form-label">{t('login.email')}</label>
                  <input
                    type="email"
                    className="auth-input"
                    placeholder={t('login.email_placeholder')}
                    name="email"
                    value={values.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <label className="form-label mb-0">{t('login.password')}</label>
                    <Link to="/forgot-password" style={{ fontSize: '0.85rem' }} className="auth-link">
                      {t('login.forgot_password')}
                    </Link>
                  </div>
                  <div className="position-relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className="auth-input"
                      placeholder={t('login.password_placeholder')}
                      name="password"
                      value={values.password}
                      onChange={handleChange}
                      required
                    />
                    <span
                      className="position-absolute top-50 end-0 translate-middle-y pe-3"
                      style={{ cursor: 'pointer', color: 'rgba(0,0,0,0.4)' }}
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                    </span>
                  </div>
                </div>

                <button type="submit" className="btn-auth-submit" disabled={loading}>
                  {loading ? (
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  ) : null}
                  {loading ? t('login.submitting') : t('login.submit')}
                </button>
              </form>

              <div className="auth-footer-text">
                {t('login.no_account')} <Link to="/register" className="auth-link">{t('login.create_account')}</Link>
                <div className="mt-3 pt-3 border-top" style={{ fontSize: '0.85rem' }}>
                  {t('login.dealer_prompt')} <Link to="/dealer-login" className="auth-link">{t('login.dealer_link')}</Link>
                </div>
              </div>
            </div>

            {/* Info Side */}
            <div className="auth-info-side">
              <div className="info-content">
                <h3 className="info-title">{t('login.info_title')}</h3>

                <div className="advantage-list">
                  <div className="advantage-item">
                    <div className="advantage-icon">
                      <i className="bi bi-check2 text-white"></i>
                    </div>
                    <div className="advantage-text">
                      <h4>{t('login.feature_dashboard_title')}</h4>
                      <p>{t('login.feature_dashboard_desc')}</p>
                    </div>
                  </div>

                  <div className="advantage-item">
                    <div className="advantage-icon">
                      <i className="bi bi-check2 text-white"></i>
                    </div>
                    <div className="advantage-text">
                      <h4>{t('login.feature_notifications_title')}</h4>
                      <p>{t('login.feature_notifications_desc')}</p>
                    </div>
                  </div>

                  <div className="advantage-item">
                    <div className="advantage-icon">
                      <i className="bi bi-check2 text-white"></i>
                    </div>
                    <div className="advantage-text">
                      <h4>{t('login.feature_sellers_title')}</h4>
                      <p>{t('login.feature_sellers_desc')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
